import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Phone, Users, Calendar, Clock, TrendingUp, Search, Bell,
  LayoutDashboard, BarChart3, Settings, ChevronRight, Play, Download,
  CheckCircle, AlertCircle, Zap, DollarSign, BookOpen, AlertTriangle,
  Building, MapPin, ChevronDown, Activity, ExternalLink, Mail, Filter,
  CreditCard, ArrowUpRight, ArrowDownRight, XCircle, FileText, HelpCircle,
  MoreHorizontal, RefreshCw, Eye, ChevronUp, LogOut
} from "lucide-react";
import { SettingsPanel } from "@/components/dashboard/SettingsPanel";
import { OnboardingGate } from "@/components/onboarding";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get stored auth data
const getAuthData = () => {
  const apiKey = localStorage.getItem('customer_api_key');
  const clientId = localStorage.getItem('customer_client_id');
  const clientName = localStorage.getItem('customer_client_name');
  const plan = localStorage.getItem('customer_plan');
  return { apiKey, clientId, clientName, plan };
};

// ============================================================================
// CALYRO ENTERPRISE DASHBOARD - PREMIUM DESIGN SYSTEM
// Clean Enterprise: Salesforce/HubSpot inspired
// Bold typography, card depth, professional palette, smooth animations
// ============================================================================

// CSS-in-JS styles for animations (added to head)
const injectStyles = () => {
  if (document.getElementById('calyro-enterprise-styles')) return;
  const style = document.createElement('style');
  style.id = 'calyro-enterprise-styles';
  style.textContent = `
    @keyframes pulse-ring {
      0% { transform: scale(0.8); opacity: 1; }
      100% { transform: scale(2); opacity: 0; }
    }
    @keyframes slide-up {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes fade-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes chart-grow {
      from { transform: scaleY(0); }
      to { transform: scaleY(1); }
    }
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    @keyframes count-up {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes draw-line {
      from { stroke-dashoffset: 1000; }
      to { stroke-dashoffset: 0; }
    }
    .animate-slide-up { animation: slide-up 0.5s ease-out forwards; }
    .animate-fade-in { animation: fade-in 0.4s ease-out forwards; }
    .animate-chart-grow { animation: chart-grow 0.8s ease-out forwards; transform-origin: bottom; }
    .animate-count-up { animation: count-up 0.6s ease-out forwards; }
    .animate-draw-line { animation: draw-line 1.5s ease-out forwards; stroke-dasharray: 1000; }
    .pulse-ring::before {
      content: '';
      position: absolute;
      inset: -4px;
      border-radius: 50%;
      border: 2px solid currentColor;
      animation: pulse-ring 2s ease-out infinite;
    }
    .card-hover { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
    .card-hover:hover { transform: translateY(-2px); box-shadow: 0 12px 24px -8px rgba(30, 58, 95, 0.15); }
    .btn-hover { transition: all 0.2s ease; }
    .btn-hover:hover { transform: translateY(-1px); }
    .btn-hover:active { transform: translateY(0); }
    .shimmer-bg {
      background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
      background-size: 200% 100%;
      animation: shimmer 2s infinite;
    }
    .area-chart-gradient {
      fill: url(#areaGradient);
    }
  `;
  document.head.appendChild(style);
};

const EnterpriseDashboardInner = () => {
  const navigate = useNavigate();
  const [billing, setBilling] = useState(null);
  const [calls, setCalls] = useState([]);
  const [leads, setLeads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [knowledgeBase, setKnowledgeBase] = useState(null);
  const [usageStats, setUsageStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeNav, setActiveNav] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [authError, setAuthError] = useState(false);

  // Get auth data from localStorage
  const { apiKey, clientId, clientName } = getAuthData();

  useEffect(() => {
    injectStyles();
    
    // Check if user is authenticated
    if (!apiKey) {
      navigate('/customer-login');
      return;
    }
  }, [apiKey, navigate]);

  const loadData = async () => {
    const { apiKey: currentApiKey, clientId: currentClientId } = getAuthData();
    
    if (!currentApiKey) {
      setAuthError(true);
      setLoading(false);
      return;
    }
    
    try {
      const headers = { "X-API-Key": currentApiKey };
      const [billingRes, callsRes, leadsRes, bookingsRes, kbRes, usageRes] = await Promise.all([
        fetch(`${API_URL}/api/billing/overview`, { headers }).catch(() => null),
        fetch(`${API_URL}/api/calls?limit=100`, { headers }).catch(() => null),
        fetch(`${API_URL}/api/leads`, { headers }).catch(() => null),
        fetch(`${API_URL}/api/bookings`, { headers }).catch(() => null),
        fetch(`${API_URL}/api/knowledge-base/${currentClientId}`, { headers }).catch(() => null),
        fetch(`${API_URL}/api/usage/stats?days=30`, { headers }).catch(() => null)
      ]);
      
      // Check for auth errors
      if (billingRes?.status === 401 || callsRes?.status === 401) {
        setAuthError(true);
        localStorage.removeItem('customer_api_key');
        navigate('/customer-login');
        return;
      }
      
      if (billingRes?.ok) setBilling(await billingRes.json());
      if (callsRes?.ok) { const d = await callsRes.json(); setCalls(d.calls || []); }
      if (leadsRes?.ok) setLeads(await leadsRes.json());
      if (bookingsRes?.ok) setBookings(await bookingsRes.json());
      if (kbRes?.ok) setKnowledgeBase(await kbRes.json());
      if (usageRes?.ok) setUsageStats(await usageRes.json());
    } catch (err) { console.error("Failed to load:", err); }
    finally { setLoading(false); setIsRefreshing(false); }
  };

  useEffect(() => {
    if (apiKey) {
      loadData();
    }
  }, [apiKey]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    loadData();
  };

  const handleLogout = () => {
    localStorage.removeItem('customer_api_key');
    localStorage.removeItem('customer_client_id');
    localStorage.removeItem('customer_client_name');
    localStorage.removeItem('customer_plan');
    localStorage.removeItem('customer_email');
    navigate('/customer-login');
  };

  // Derived data
  const minutesUsed = Math.round(billing?.usage?.minutes_used || 0);
  const minutesIncluded = billing?.usage?.minutes_included || 800;
  const hardCap = billing?.plan?.hard_cap_minutes || 1000;
  const usagePercent = Math.min(Math.round((minutesUsed / minutesIncluded) * 100), 150);
  const canAcceptCalls = billing?.usage?.allowed !== false;
  const agentStatus = !canAcceptCalls ? 'blocked' : usagePercent >= 90 ? 'limited' : 'live';
  const planName = billing?.plan?.name || billing?.plan?.plan_id || "Frontdesk";
  const displayName = clientName || "Customer";

  const navItems = [
    { id: 'overview', icon: LayoutDashboard, label: 'Overview' },
    { id: 'calls', icon: Phone, label: 'Calls' },
    { id: 'leads', icon: Users, label: 'Leads' },
    { id: 'bookings', icon: Calendar, label: 'Bookings' },
    { id: 'usage', icon: BarChart3, label: 'Usage' },
    { id: 'billing', icon: DollarSign, label: 'Billing' },
    { id: 'knowledge', icon: BookOpen, label: 'Knowledge Base' },
    { id: 'settings', icon: Settings, label: 'Settings' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E9F0FB] flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-[#D6E2F3] rounded-full"></div>
            <div className="w-16 h-16 border-4 border-[#4F80F5] border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
          </div>
          <p className="mt-6 text-[#4A4A4A] font-medium text-lg">Loading your dashboard...</p>
          <p className="mt-2 text-[#8A8A8A] text-sm">Fetching latest data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E9F0FB] flex">
      {/* ════════════════════════════════════════════════════════════════════
          LEFT SIDEBAR - Dark Navy Blue (#0F2FA8)
      ════════════════════════════════════════════════════════════════════ */}
      <aside className="w-56 bg-[#0F2FA8] flex flex-col min-h-screen">
        {/* Company Header */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-[#4F80F5] flex items-center justify-center">
              <Building className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm truncate">{displayName}</p>
              <p className="text-white/50 text-xs truncate">{planName} Plan</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4">
          <ul className="space-y-1">
            {navItems.map((item, index) => (
              <li key={item.id} style={{ animationDelay: `${index * 50}ms` }} className="animate-fade-in">
                <button
                  onClick={() => setActiveNav(item.id)}
                  className={`w-full flex items-center gap-3 px-5 py-3 transition-all duration-200 ${
                    activeNav === item.id
                      ? 'bg-[#4F80F5] text-white'
                      : 'text-white/70 hover:text-white hover:bg-[#2F6FE4]/50'
                  }`}
                >
                  <item.icon className={`w-5 h-5 ${activeNav === item.id ? 'text-[#6EDBC5]' : ''}`} />
                  <span className="font-medium text-sm">{item.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t border-white/10">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 text-white/70 hover:text-white hover:bg-red-500/20 rounded-lg transition-all duration-200"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium text-sm">Logout</span>
          </button>
        </div>
      </aside>

      {/* ════════════════════════════════════════════════════════════════════
          MAIN CONTENT AREA
      ════════════════════════════════════════════════════════════════════ */}
      <main className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* TOP BAR - Light Blue */}
        <header className="bg-[#D6E2F3] px-6 py-3 sticky top-0 z-20">
          <div className="flex items-center justify-between">
            {/* Search */}
            <div className="flex-1 max-w-sm">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8A]" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white border border-[#D6E2F3] rounded text-[#4A4A4A] placeholder-[#8A8A8A] focus:outline-none focus:ring-1 focus:ring-[#4F80F5]/30 transition-all"
                />
              </div>
            </div>

            {/* Right side - User Profile */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-[#4A4A4A] uppercase">Your Name</span>
              <div className="w-10 h-10 rounded-full bg-[#E9F0FB] border-2 border-[#D6E2F3] flex items-center justify-center">
                <Users className="w-5 h-5 text-[#8A8A8A]" />
              </div>
              <ChevronDown className="w-4 h-4 text-[#8A8A8A]" />
            </div>
          </div>
        </header>

        {/* Welcome Banner - Only show on Overview */}
        {activeNav === 'overview' && (
          <WelcomeBanner 
            clientName={displayName} 
            calls={calls} 
            usagePercent={usagePercent}
            agentStatus={agentStatus}
          />
        )}

        {/* Page Content */}
        <div className="flex-1 p-8 overflow-auto">
          <div className="max-w-7xl mx-auto">
            {activeNav === 'overview' && <OverviewTab billing={billing} calls={calls} leads={leads} minutesUsed={minutesUsed} minutesIncluded={minutesIncluded} usagePercent={usagePercent} agentStatus={agentStatus} />}
            {activeNav === 'calls' && <CallsTab calls={calls} searchQuery={searchQuery} />}
            {activeNav === 'leads' && <LeadsTab leads={leads} />}
            {activeNav === 'bookings' && <BookingsTab bookings={bookings} />}
            {activeNav === 'usage' && <UsageTab billing={billing} minutesUsed={minutesUsed} minutesIncluded={minutesIncluded} hardCap={hardCap} usagePercent={usagePercent} usageStats={usageStats} />}
            {activeNav === 'billing' && <BillingTab billing={billing} planName={planName} />}
            {activeNav === 'knowledge' && <KnowledgeBaseTab />}
            {activeNav === 'settings' && <SettingsPanel />}
          </div>
        </div>
      </main>
    </div>
  );
};

// Export with OnboardingGate wrapper
export const EnterpriseDashboard = () => (
  <OnboardingGate>
    <EnterpriseDashboardInner />
  </OnboardingGate>
);

// ════════════════════════════════════════════════════════════════════════════
// WELCOME BANNER COMPONENT
// ════════════════════════════════════════════════════════════════════════════
const WelcomeBanner = ({ clientName, calls, usagePercent, agentStatus }) => {
  const todayCalls = calls.filter(c => {
    if (!c.created_at) return false;
    const callDate = new Date(c.created_at).toDateString();
    return callDate === new Date().toDateString();
  }).length;

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const getStatusColor = () => {
    if (agentStatus === 'live') return 'bg-green-500';
    if (agentStatus === 'limited') return 'bg-amber-500';
    return 'bg-red-500';
  };

  const getStatusText = () => {
    if (agentStatus === 'live') return 'AI Agent Active';
    if (agentStatus === 'limited') return 'Approaching Limit';
    return 'Agent Paused';
  };

  return (
    <div className="mx-6 mt-4">
      <div className="bg-gradient-to-r from-[#0F2FA8] to-[#4F80F5] rounded-xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between">
          {/* Left side - Greeting */}
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
              <span className="text-2xl">👋</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold">
                {getGreeting()}, {clientName}!
              </h1>
              <p className="text-white/80 text-sm mt-1">
                {todayCalls > 0 
                  ? `Your AI handled ${todayCalls} call${todayCalls !== 1 ? 's' : ''} today`
                  : "Your AI is ready to handle calls"
                }
              </p>
            </div>
          </div>

          {/* Right side - Quick Stats */}
          <div className="flex items-center gap-6">
            {/* Agent Status */}
            <div className="text-center">
              <div className="flex items-center gap-2 justify-center">
                <span className={`w-3 h-3 rounded-full ${getStatusColor()} ${agentStatus === 'live' ? 'animate-pulse' : ''}`}></span>
                <span className="text-sm font-medium">{getStatusText()}</span>
              </div>
              <p className="text-white/60 text-xs mt-1">Status</p>
            </div>

            {/* Usage */}
            <div className="text-center border-l border-white/20 pl-6">
              <div className="text-2xl font-bold">{usagePercent}%</div>
              <p className="text-white/60 text-xs">Usage This Month</p>
            </div>

            {/* Quick Actions */}
            <div className="border-l border-white/20 pl-6">
              <button className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors flex items-center gap-2">
                <Activity className="w-4 h-4" />
                View Activity
              </button>
            </div>
          </div>
        </div>

        {/* Usage Warning - Show if above 80% */}
        {usagePercent >= 80 && (
          <div className="mt-4 pt-4 border-t border-white/20">
            <div className="flex items-center gap-3 bg-amber-500/20 rounded-lg px-4 py-3">
              <AlertTriangle className="w-5 h-5 text-amber-300" />
              <span className="text-sm">
                You've used {usagePercent}% of your monthly minutes. 
                <a href="#" className="underline ml-1 font-medium hover:text-amber-200">Upgrade your plan</a> to avoid service interruption.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// TAB 1: OVERVIEW - Executive Command Center
// ════════════════════════════════════════════════════════════════════════════
const OverviewTab = ({ billing, calls, leads, minutesUsed, minutesIncluded, usagePercent, agentStatus }) => {
  const todayCalls = calls.filter(c => {
    if (!c.created_at) return false;
    const callDate = new Date(c.created_at).toDateString();
    return callDate === new Date().toDateString();
  }).length;

  const thisWeekCalls = calls.filter(c => {
    if (!c.created_at) return false;
    const callDate = new Date(c.created_at);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return callDate >= weekAgo;
  }).length;

  const newLeads = leads.filter(l => l.status === 'new').length;
  const conversionRate = calls.length > 0 ? Math.round((leads.length / calls.length) * 100) : 87;

  // Monthly data for line chart
  const monthlyData = [
    { month: 'JAN', value: 150 },
    { month: 'FEB', value: 280 },
    { month: 'MAR', value: 90 },
    { month: 'APR', value: 180 },
    { month: 'MAY', value: 400 },
    { month: 'JUN', value: 320 },
  ];

  // Alerts
  const alerts = [];
  if (usagePercent >= 72) alerts.push({ type: 'warning', message: `${usagePercent}% of monthly usage consumed` });
  if (billing?.client?.billing_status === 'grace') alerts.push({ type: 'critical', message: 'Billing grace period active - action required' });
  if (todayCalls > 0) alerts.push({ type: 'success', message: `${todayCalls} calls handled today` });

  return (
    <div className="space-y-6">
      {/* KPI CARDS - 4 columns like reference */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICardBlue title="CONVERSION RATE" subtitle="DOLOR SIT AMET" value={`${conversionRate} %`} />
        <KPICardBlue title="TOTAL CALLS" subtitle="DOLOR SIT AMET" value={thisWeekCalls > 0 ? thisWeekCalls.toLocaleString() : '1,503'} />
        <KPICardBlue title="NEW LEADS" subtitle="DOLOR SIT AMET" value={newLeads > 0 ? newLeads.toLocaleString() : '2,450'} />
        <KPICardBlue title="MINUTES USED" subtitle="DOLOR SIT AMET" value={minutesUsed > 0 ? minutesUsed.toLocaleString() : '4,220'} />
      </div>

      {/* CHARTS ROW - Line chart + Donut */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Line Chart - spans 2 columns */}
        <div className="lg:col-span-2 card-hover bg-white rounded-xl p-6 shadow-sm border border-[#d4e8f2]">
          <h3 className="text-sm font-bold text-[#4A4A4A] uppercase tracking-wider mb-6">Call Activity</h3>
          <LineChartComponent data={monthlyData} />
        </div>

        {/* Donut Chart */}
        <div className="card-hover bg-white rounded-xl p-6 shadow-sm border border-[#d4e8f2]">
          <UsageDonutChart used={minutesUsed || 75} total={minutesIncluded || 100} />
        </div>
      </div>

      {/* BOTTOM ROW - 4 Area Charts */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <AreaChartCard title="CALLS HANDLED" subtitle="DOLOR SIT AMET" color1="#2196f3" color2="#6EDBC5" />
        <AreaChartCard title="LEADS CAPTURED" subtitle="DOLOR SIT AMET" color1="#6EDBC5" color2="#9AF2B4" />
        <AreaChartCard title="BOOKINGS MADE" subtitle="DOLOR SIT AMET" color1="#6EDBC5" color2="#6EDBC5" />
        <ActivityListCard alerts={alerts} />
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// TAB 2: CALLS - Audit & Trust
// ════════════════════════════════════════════════════════════════════════════
const CallsTab = ({ calls, searchQuery }) => {
  const [expandedCall, setExpandedCall] = useState(null);

  // Calculate stats
  const todayCalls = calls.filter(c => {
    if (!c.created_at) return false;
    return new Date(c.created_at).toDateString() === new Date().toDateString();
  }).length;

  const avgDuration = calls.length > 0 
    ? Math.round(calls.reduce((sum, c) => sum + (c.duration_seconds || 0), 0) / calls.length)
    : 145;

  const bookedCalls = calls.filter(c => c.outcome === 'booked').length;

  const filteredCalls = calls.filter(call => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!call.caller_number?.includes(q) && !call.caller_name?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // Demo data if no calls
  const displayCalls = filteredCalls.length > 0 ? filteredCalls : [
    { id: 1, caller_name: 'John Smith', caller_number: '+1 555-0101', created_at: new Date().toISOString(), duration_seconds: 180, intent: 'booking', outcome: 'booked' },
    { id: 2, caller_name: 'Sarah Johnson', caller_number: '+1 555-0102', created_at: new Date().toISOString(), duration_seconds: 95, intent: 'inquiry', outcome: 'lead' },
    { id: 3, caller_name: 'Mike Davis', caller_number: '+1 555-0103', created_at: new Date().toISOString(), duration_seconds: 210, intent: 'booking', outcome: 'booked' },
    { id: 4, caller_name: 'Emily Brown', caller_number: '+1 555-0104', created_at: new Date().toISOString(), duration_seconds: 65, intent: 'general', outcome: 'info' },
  ];

  return (
    <div className="space-y-4">
      {/* Stats Row - 4 KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <KPICardBlue title="TODAY'S CALLS" subtitle="HANDLED BY AI" value={todayCalls || '24'} />
        <KPICardBlue title="TOTAL CALLS" subtitle="THIS WEEK" value={calls.length || '156'} />
        <KPICardBlue title="AVG DURATION" subtitle="SECONDS" value={`${avgDuration}s`} />
        <KPICardBlue title="BOOKINGS" subtitle="CONVERTED" value={bookedCalls || '18'} />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Call Volume Chart */}
        <div className="lg:col-span-2 bg-white rounded-lg p-5 border border-[#D6E2F3]">
          <h3 className="text-xs font-bold text-[#4F80F5] uppercase tracking-wider mb-4">Call Volume</h3>
          <LineChartComponent data={[
            { month: 'MON', value: 25 },
            { month: 'TUE', value: 32 },
            { month: 'WED', value: 28 },
            { month: 'THU', value: 45 },
            { month: 'FRI', value: 38 },
            { month: 'SAT', value: 15 },
          ]} />
        </div>

        {/* Call Outcomes Donut */}
        <div className="bg-white rounded-lg p-5 border border-[#D6E2F3]">
          <h3 className="text-xs font-bold text-[#4F80F5] uppercase tracking-wider mb-4">Call Outcomes</h3>
          <UsageDonutChart used={bookedCalls || 65} total={calls.length || 100} />
          <div className="flex justify-center gap-4 mt-4 text-xs">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#6EDBC5]"></span> <span className="text-[#8A8A8A]">Booked</span></span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#4F80F5]"></span> <span className="text-[#8A8A8A]">Lead</span></span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#D6E2F3]"></span> <span className="text-[#8A8A8A]">Info</span></span>
          </div>
        </div>
      </div>

      {/* Bottom Row - Area Charts + Recent Calls */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <AreaChartCard title="PEAK HOURS" subtitle="CALL DISTRIBUTION" />
        <AreaChartCard title="CALL DURATION" subtitle="TREND ANALYSIS" />
        
        {/* Recent Calls Quick View */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-[#D6E2F3] overflow-hidden">
          <div className="px-4 py-3 bg-[#E9F0FB] flex items-center justify-between">
            <h3 className="text-xs font-bold text-[#4F80F5] uppercase tracking-wider">Recent Calls</h3>
            <button className="px-3 py-1 text-xs font-medium text-[#6EDBC5] bg-[#6EDBC5]/10 rounded hover:bg-[#6EDBC5]/20">
              <Download className="w-3 h-3 inline mr-1" /> Export
            </button>
          </div>
          <table className="w-full">
            <thead className="bg-[#E9F0FB]/50">
              <tr>
                <th className="text-left text-xs font-bold text-[#4F80F5] uppercase px-4 py-2">Caller</th>
                <th className="text-left text-xs font-bold text-[#4F80F5] uppercase px-4 py-2">Duration</th>
                <th className="text-left text-xs font-bold text-[#4F80F5] uppercase px-4 py-2">Outcome</th>
                <th className="text-left text-xs font-bold text-[#4F80F5] uppercase px-4 py-2">Play</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#D6E2F3]">
              {displayCalls.slice(0, 4).map((call) => (
                <tr key={call.id} className="hover:bg-[#E9F0FB]/50 transition-colors">
                  <td className="px-4 py-2">
                    <p className="text-sm font-semibold text-[#4A4A4A]">{call.caller_name || 'Unknown'}</p>
                    <p className="text-xs text-[#8A8A8A]">{call.caller_number || '-'}</p>
                  </td>
                  <td className="px-4 py-2 text-sm text-[#4A4A4A]">{formatDuration(call.duration_seconds)}</td>
                  <td className="px-4 py-2"><OutcomeBadge outcome={call.outcome} /></td>
                  <td className="px-4 py-2">
                    <button className="p-1.5 hover:bg-[#6EDBC5]/10 rounded text-[#6EDBC5]">
                      <Play className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// TAB 3: LEADS - Revenue Impact
// ════════════════════════════════════════════════════════════════════════════
const LeadsTab = ({ leads }) => {
  return (
    <div className="space-y-4">
      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <KPICardBlue title="NEW LEADS" subtitle="DOLOR SIT AMET" value={leads.filter(l => l.status === 'new').length || '12'} />
        <KPICardBlue title="CONTACTED" subtitle="DOLOR SIT AMET" value={leads.filter(l => l.status === 'contacted').length || '8'} />
        <KPICardBlue title="QUALIFIED" subtitle="DOLOR SIT AMET" value="5" />
        <KPICardBlue title="TOTAL LEADS" subtitle="DOLOR SIT AMET" value={leads.length || '25'} />
      </div>

      {/* Main Content Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Leads Chart */}
        <div className="lg:col-span-2 bg-white rounded-lg p-5 border border-[#D6E2F3]">
          <h3 className="text-xs font-bold text-[#4F80F5] uppercase tracking-wider mb-4">Leads Over Time</h3>
          <LineChartComponent data={[
            { month: 'JAN', value: 8 },
            { month: 'FEB', value: 15 },
            { month: 'MAR', value: 12 },
            { month: 'APR', value: 20 },
            { month: 'MAY', value: 25 },
            { month: 'JUN', value: 18 },
          ]} />
        </div>

        {/* Lead Sources */}
        <div className="bg-white rounded-lg p-5 border border-[#D6E2F3]">
          <h3 className="text-xs font-bold text-[#4F80F5] uppercase tracking-wider mb-4">Lead Sources</h3>
          <UsageDonutChart used={75} total={100} />
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-lg border border-[#D6E2F3] overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#E9F0FB]">
            <tr>
              <th className="text-left text-xs font-bold text-[#4F80F5] uppercase px-4 py-3">Name</th>
              <th className="text-left text-xs font-bold text-[#4F80F5] uppercase px-4 py-3">Phone</th>
              <th className="text-left text-xs font-bold text-[#4F80F5] uppercase px-4 py-3">Source</th>
              <th className="text-left text-xs font-bold text-[#4F80F5] uppercase px-4 py-3">Status</th>
              <th className="text-left text-xs font-bold text-[#4F80F5] uppercase px-4 py-3">Date</th>
              <th className="text-left text-xs font-bold text-[#4F80F5] uppercase px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E9F0FB]">
            {(leads.length === 0 ? [
              { id: 1, name: 'John Smith', phone: '+1 555-0101', status: 'new', created_at: new Date().toISOString() },
              { id: 2, name: 'Sarah Johnson', phone: '+1 555-0102', status: 'contacted', created_at: new Date().toISOString() },
              { id: 3, name: 'Mike Davis', phone: '+1 555-0103', status: 'new', created_at: new Date().toISOString() },
            ] : leads).map((lead) => (
              <tr key={lead.id} className="hover:bg-[#f5f8fc] transition-colors">
                <td className="px-4 py-3 text-sm font-semibold text-[#4F80F5]">{lead.name || 'Unknown'}</td>
                <td className="px-4 py-3 text-sm text-[#374151]">{lead.phone || '-'}</td>
                <td className="px-4 py-3 text-sm text-[#374151]">AI Call</td>
                <td className="px-4 py-3"><StatusBadge status={lead.status} /></td>
                <td className="px-4 py-3 text-sm text-[#6B7280]">{lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '-'}</td>
                <td className="px-4 py-3">
                  <button className="px-3 py-1 text-xs font-medium text-[#6EDBC5] bg-[#6EDBC5]/10 rounded hover:bg-[#6EDBC5]/20">
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// TAB 4: BOOKINGS
// ════════════════════════════════════════════════════════════════════════════
const BookingsTab = ({ bookings }) => {
  return (
    <div className="space-y-4">
      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <KPICardBlue title="TODAY" subtitle="DOLOR SIT AMET" value="8" />
        <KPICardBlue title="THIS WEEK" subtitle="DOLOR SIT AMET" value="32" />
        <KPICardBlue title="CONFIRMED" subtitle="DOLOR SIT AMET" value="28" />
        <KPICardBlue title="PENDING" subtitle="DOLOR SIT AMET" value="4" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-lg p-5 border border-[#D6E2F3]">
          <h3 className="text-xs font-bold text-[#4F80F5] uppercase tracking-wider mb-4">Bookings Trend</h3>
          <LineChartComponent data={[
            { month: 'JAN', value: 20 },
            { month: 'FEB', value: 35 },
            { month: 'MAR', value: 28 },
            { month: 'APR', value: 45 },
            { month: 'MAY', value: 52 },
            { month: 'JUN', value: 40 },
          ]} />
        </div>
        <div className="bg-white rounded-lg p-5 border border-[#D6E2F3]">
          <h3 className="text-xs font-bold text-[#4F80F5] uppercase tracking-wider mb-4">By Service Type</h3>
          <UsageDonutChart used={65} total={100} />
        </div>
      </div>

      {/* Bookings Table */}
      <div className="bg-white rounded-lg border border-[#D6E2F3] overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#E9F0FB]">
            <tr>
              <th className="text-left text-xs font-bold text-[#4F80F5] uppercase px-4 py-3">Caller</th>
              <th className="text-left text-xs font-bold text-[#4F80F5] uppercase px-4 py-3">Service</th>
              <th className="text-left text-xs font-bold text-[#4F80F5] uppercase px-4 py-3">Provider</th>
              <th className="text-left text-xs font-bold text-[#4F80F5] uppercase px-4 py-3">Date & Time</th>
              <th className="text-left text-xs font-bold text-[#4F80F5] uppercase px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E9F0FB]">
            {(bookings.length === 0 ? [
              { id: 1, caller_name: 'John Smith', service: 'Dental Checkup', provider: 'Dr. Wilson', scheduled_for: new Date().toISOString(), status: 'confirmed' },
              { id: 2, caller_name: 'Sarah Johnson', service: 'Cleaning', provider: 'Dr. Miller', scheduled_for: new Date().toISOString(), status: 'pending' },
              { id: 3, caller_name: 'Mike Davis', service: 'Consultation', provider: 'Dr. Wilson', scheduled_for: new Date().toISOString(), status: 'confirmed' },
            ] : bookings).map((booking) => (
              <tr key={booking.id} className="hover:bg-[#f5f8fc] transition-colors">
                <td className="px-4 py-3 text-sm font-semibold text-[#4F80F5]">{booking.caller_name || 'Unknown'}</td>
                <td className="px-4 py-3 text-sm text-[#374151]">{booking.service || booking.service_type || '-'}</td>
                <td className="px-4 py-3 text-sm text-[#374151]">{booking.provider || '-'}</td>
                <td className="px-4 py-3 text-sm text-[#374151]">{booking.scheduled_for ? new Date(booking.scheduled_for).toLocaleString() : '-'}</td>
                <td className="px-4 py-3"><BookingStatusBadge status={booking.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// TAB 5: USAGE
// ════════════════════════════════════════════════════════════════════════════
const UsageTab = ({ billing, minutesUsed, minutesIncluded, hardCap, usagePercent, usageStats }) => {
  const overageMinutes = Math.max(0, minutesUsed - minutesIncluded);
  const overageRate = billing?.plan?.overage_per_min || 0.15;
  const estimatedOverage = overageMinutes * overageRate;

  return (
    <div className="space-y-4">
      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <KPICardBlue title="MINUTES USED" subtitle="THIS CYCLE" value={minutesUsed || '245'} />
        <KPICardBlue title="INCLUDED" subtitle="IN PLAN" value={minutesIncluded || '800'} />
        <KPICardBlue title="REMAINING" subtitle="AVAILABLE" value={Math.max(0, (minutesIncluded || 800) - (minutesUsed || 245))} />
        <KPICardBlue title="USAGE %" subtitle="OF PLAN" value={`${usagePercent || 31}%`} />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-lg p-5 border border-[#D6E2F3]">
          <h3 className="text-xs font-bold text-[#4F80F5] uppercase tracking-wider mb-4">Usage Over Time</h3>
          <LineChartComponent data={[
            { month: 'JAN', value: 150 },
            { month: 'FEB', value: 280 },
            { month: 'MAR', value: 200 },
            { month: 'APR', value: 350 },
            { month: 'MAY', value: 420 },
            { month: 'JUN', value: 320 },
          ]} />
        </div>
        <div className="bg-white rounded-lg p-5 border border-[#D6E2F3]">
          <h3 className="text-xs font-bold text-[#4F80F5] uppercase tracking-wider mb-4">Usage Breakdown</h3>
          <UsageDonutChart used={minutesUsed || 245} total={minutesIncluded || 800} />
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <AreaChartCard title="DAILY USAGE" subtitle="LAST 7 DAYS" color1="#2196f3" color2="#6EDBC5" />
        <AreaChartCard title="PEAK HOURS" subtitle="BY TIME" color1="#6EDBC5" color2="#9AF2B4" />
        <div className="bg-white rounded-lg p-5 border border-[#D6E2F3]">
          <h4 className="text-xs font-bold text-[#4F80F5] uppercase tracking-wider">Overage Details</h4>
          <p className="text-xs text-[#6B7280] uppercase mb-3">Cost Breakdown</p>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-[#4B5563]">Overage Minutes</span><span className="font-semibold text-[#4F80F5]">{overageMinutes} min</span></div>
            <div className="flex justify-between"><span className="text-[#4B5563]">Rate</span><span className="font-semibold text-[#4F80F5]">${overageRate.toFixed(2)}/min</span></div>
            <div className="flex justify-between pt-2 border-t border-[#E9F0FB]"><span className="text-[#4B5563]">Est. Cost</span><span className="font-bold text-[#6EDBC5]">${estimatedOverage.toFixed(2)}</span></div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-5 border border-[#D6E2F3]">
          <h4 className="text-xs font-bold text-[#4F80F5] uppercase tracking-wider">Usage Zones</h4>
          <p className="text-xs text-[#6B7280] uppercase mb-3">Status</p>
          <div className="space-y-2">
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#6EDBC5]"></span><span className="text-xs text-[#4F80F5]">0-70% Normal</span></div>
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-amber-400"></span><span className="text-xs text-[#4F80F5]">70-90% Caution</span></div>
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-orange-500"></span><span className="text-xs text-[#4F80F5]">90-100% Warning</span></div>
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-red-500"></span><span className="text-xs text-[#4F80F5]">&gt;100% Overage</span></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// TAB 6: BILLING - Enterprise Ready
// ════════════════════════════════════════════════════════════════════════════
const BillingTab = ({ billing, planName }) => {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const { plan, usage, client } = billing || {};
  
  const minutesUsed = Math.round(usage?.minutes_used || 0);
  const minutesIncluded = usage?.minutes_included || plan?.monthly_minutes_included || 800;
  const hardCap = plan?.hard_cap_minutes || 1000;
  const overageRate = plan?.overage_per_min || 0.45;
  const overageMinutes = Math.max(0, minutesUsed - minutesIncluded);
  const overageCost = overageMinutes * overageRate;
  const basePrice = plan?.price_monthly || 349;
  const estimatedInvoice = basePrice + overageCost;
  const usagePercent = Math.round((minutesUsed / minutesIncluded) * 100);
  
  const getUsageStatus = () => {
    if (usagePercent >= 100) return { color: 'red', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: '🔴', message: "You're in overage - additional charges apply" };
    if (usagePercent >= 90) return { color: 'orange', bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', icon: '🟠', message: "Warning: You're near your limit" };
    if (usagePercent >= 70) return { color: 'amber', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: '🟡', message: "Caution: Approaching your limit" };
    return { color: 'emerald', bg: 'bg-[#6EDBC5]/10', border: 'border-[#6EDBC5]/30', text: 'text-[#4F80F5]', icon: '🟢', message: "You're within safe usage" };
  };
  const usageStatus = getUsageStatus();

  const today = new Date();
  const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const resetDate = usage?.cycle_end ? new Date(usage.cycle_end) : nextMonth;
  
  const invoiceHistory = [
    { date: 'Dec 2025', amount: basePrice, status: 'pending' },
    { date: 'Nov 2025', amount: basePrice, status: 'paid' },
    { date: 'Oct 2025', amount: basePrice, status: 'paid' },
  ];

  return (
    <div className="space-y-4">

      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <KPICardBlue title="CURRENT PLAN" subtitle={planName.toUpperCase()} value={`$${basePrice}`} />
        <KPICardBlue title="NEXT INVOICE" subtitle="DUE DATE" value={`$${estimatedInvoice.toFixed(0)}`} />
        <KPICardBlue title="MINUTES USED" subtitle="THIS CYCLE" value={minutesUsed} />
        <KPICardBlue title="USAGE" subtitle="PERCENTAGE" value={`${usagePercent}%`} />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-lg p-5 border border-[#D6E2F3]">
          <h3 className="text-xs font-bold text-[#4F80F5] uppercase tracking-wider mb-4">Billing History</h3>
          <LineChartComponent data={[
            { month: 'JAN', value: 349 },
            { month: 'FEB', value: 349 },
            { month: 'MAR', value: 380 },
            { month: 'APR', value: 349 },
            { month: 'MAY', value: 420 },
            { month: 'JUN', value: 349 },
          ]} />
        </div>
        <div className="bg-white rounded-lg p-5 border border-[#D6E2F3]">
          <h3 className="text-xs font-bold text-[#4F80F5] uppercase tracking-wider mb-4">Usage This Cycle</h3>
          <UsageDonutChart used={minutesUsed} total={minutesIncluded} />
          <p className={`text-center text-xs mt-3 ${usageStatus.text}`}>{usageStatus.message}</p>
        </div>
      </div>

      {/* Bottom Row - Status, Cycle, Invoice History */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg p-5 border border-[#D6E2F3]">
          <h3 className="text-xs font-bold text-[#4F80F5] uppercase tracking-wider mb-4">Account Status</h3>
          <div className="space-y-3">
            <StatusRow label="Setup Fee" status={client?.setup_fee_status || 'paid'} />
            <StatusRow label="Subscription" status={client?.subscription_status || 'active'} />
            <StatusRow label="Billing Status" status={client?.billing_status || 'ok'} />
          </div>
        </div>

        <div className="bg-white rounded-lg p-5 border border-[#D6E2F3]">
          <h3 className="text-xs font-bold text-[#4F80F5] uppercase tracking-wider mb-4">Billing Cycle</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-[#4B5563]">Resets on</span><span className="font-semibold text-[#4F80F5]">{resetDate.toLocaleDateString()}</span></div>
            <div className="flex justify-between"><span className="text-[#4B5563]">Next charge</span><span className="font-semibold text-[#4F80F5]">{resetDate.toLocaleDateString()}</span></div>
            <div className="flex justify-between"><span className="text-[#4B5563]">Payment</span><span className="font-semibold text-[#4F80F5]">•••• 4242</span></div>
            <div className="flex justify-between"><span className="text-[#4B5563]">Grace period</span><span className="font-semibold text-[#4F80F5]">72 hours</span></div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-5 border border-[#D6E2F3]">
          <h3 className="text-xs font-bold text-[#4F80F5] uppercase tracking-wider mb-4">Quick Actions</h3>
          <div className="space-y-2">
            <button className="w-full py-2 text-xs font-medium text-white bg-gradient-to-r from-[#6EDBC5] to-[#6EDBC5] rounded hover:opacity-90">
              Upgrade Plan
            </button>
            <button className="w-full py-2 text-xs font-medium text-[#4F80F5] bg-[#E9F0FB] rounded hover:bg-[#d8e4f0]">
              Download Invoice
            </button>
            <button 
              onClick={() => setShowCancelModal(true)}
              className="w-full py-2 text-xs font-medium text-red-600 bg-red-50 rounded hover:bg-red-100"
            >
              Cancel Subscription
            </button>
          </div>
        </div>
      </div>

      {/* Invoice History Table */}
      <div className="bg-white rounded-lg border border-[#D6E2F3] overflow-hidden">
        <div className="px-4 py-3 bg-[#E9F0FB] flex items-center justify-between">
          <h3 className="text-xs font-bold text-[#4F80F5] uppercase tracking-wider">Invoice History</h3>
          <button className="px-3 py-1 text-xs font-medium text-[#6EDBC5] bg-[#6EDBC5]/10 rounded hover:bg-[#6EDBC5]/20">
            Download All
          </button>
        </div>
        <table className="w-full">
          <thead className="bg-[#f5f8fc]">
            <tr>
              <th className="text-left text-xs font-bold text-[#374151] uppercase px-4 py-3">Period</th>
              <th className="text-left text-xs font-bold text-[#374151] uppercase px-4 py-3">Amount</th>
              <th className="text-left text-xs font-bold text-[#374151] uppercase px-4 py-3">Status</th>
              <th className="text-right text-xs font-bold text-[#374151] uppercase px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E9F0FB]">
            {invoiceHistory.map((invoice, i) => (
              <tr key={i} className="hover:bg-[#f5f8fc] transition-colors">
                <td className="px-4 py-3 text-sm font-semibold text-[#4F80F5]">{invoice.date}</td>
                <td className="px-4 py-3 text-sm text-[#374151]">${invoice.amount.toFixed(2)}</td>
                <td className="px-4 py-3"><InvoiceStatusBadge status={invoice.status} /></td>
                <td className="px-4 py-3 text-right">
                  <button className="text-xs text-[#6EDBC5] hover:text-[#4F80F5] font-semibold">Download</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-3xl max-w-md w-full p-8 shadow-2xl animate-slide-up">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-7 h-7 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900">Cancel Subscription?</h3>
            </div>
            
            <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-5 mb-6">
              <p className="text-red-800 font-semibold mb-4">⚠️ Canceling your subscription will:</p>
              <ul className="space-y-3 text-red-700">
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>Stop your AI agent from answering calls</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>Disable new call handling immediately</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>Preserve call history for 30 days only</span>
                </li>
              </ul>
            </div>

            <p className="text-slate-600 mb-8">
              This action cannot be undone. You will lose access to your AI receptionist and all active configurations.
            </p>

            <div className="flex gap-4">
              <button 
                onClick={() => setShowCancelModal(false)}
                className="flex-1 px-5 py-3.5 bg-slate-100 text-slate-700 font-semibold rounded-2xl hover:bg-slate-200 transition-all"
              >
                Keep Subscription
              </button>
              <button 
                onClick={() => {
                  setShowCancelModal(false);
                  alert('Cancellation request submitted.');
                }}
                className="flex-1 px-5 py-3.5 bg-red-600 text-white font-semibold rounded-2xl hover:bg-red-700 transition-all"
              >
                Yes, Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// TAB 7: KNOWLEDGE BASE
// ════════════════════════════════════════════════════════════════════════════
const KnowledgeBaseTab = () => {
  const articles = [
    { title: 'How calls are handled', content: 'Your AI receptionist answers calls instantly, gathers caller information, and either books appointments or captures leads.', icon: Phone },
    { title: 'What happens at usage limits', content: 'When you reach your included minutes, overage charges apply. At the hard cap, calls may be limited until next cycle.', icon: BarChart3 },
    { title: 'Why the setup fee exists', content: 'The setup fee covers AI training, voice calibration, and knowledge base configuration for your business.', icon: Settings },
    { title: 'Billing rules explained', content: 'Billed monthly for base subscription. Overage calculated at cycle end. Upgrades prorated; downgrades next cycle.', icon: DollarSign },
    { title: 'What happens if payment fails', content: 'You enter a 48-hour grace period. After grace, calls are blocked until payment is resolved.', icon: AlertTriangle },
    { title: 'How to upgrade your plan', content: 'Click "Upgrade Plan" in Billing. Upgrades are effective immediately with prorated charges.', icon: TrendingUp },
  ];

  return (
    <div className="space-y-4">
      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <KPICardBlue title="ARTICLES" subtitle="TOTAL COUNT" value="6" />
        <KPICardBlue title="CATEGORIES" subtitle="ORGANIZED" value="3" />
        <KPICardBlue title="LAST UPDATED" subtitle="DAYS AGO" value="2" />
        <KPICardBlue title="HELP TICKETS" subtitle="THIS MONTH" value="0" />
      </div>

      {/* Articles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {articles.map((article, i) => (
          <div key={i} className="bg-white rounded-lg p-5 border border-[#D6E2F3] hover:shadow-md transition-all">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#6EDBC5] to-[#9AF2B4] flex items-center justify-center flex-shrink-0">
                <article.icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-[#4F80F5] text-sm">{article.title}</h3>
                <p className="text-[#4B5563] mt-2 text-xs leading-relaxed line-clamp-3">{article.content}</p>
                <button className="mt-3 text-xs font-medium text-[#6EDBC5] hover:text-[#4F80F5]">Read more →</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Help Card */}
      <div className="bg-gradient-to-r from-[#4F80F5] to-[#2F6FE4] rounded-lg p-6 text-white">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-white/20 flex items-center justify-center">
            <HelpCircle className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-lg">Need more help?</h3>
            <p className="text-white/70 text-sm">Contact our support team for personalized assistance.</p>
          </div>
          <a href="mailto:support@calyro.ai" className="px-4 py-2 bg-white text-[#4F80F5] font-semibold rounded-lg text-sm hover:bg-white/90 transition-colors">
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// TAB 8: SETTINGS
// ════════════════════════════════════════════════════════════════════════════
const SettingsTab = ({ planName }) => {
  const [emailNotifs, setEmailNotifs] = useState(true);
  const [smsNotifs, setSmsNotifs] = useState(false);
  const [usageAlerts, setUsageAlerts] = useState(true);

  return (
    <div className="space-y-4">
      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <KPICardBlue title="ACCOUNT" subtitle="STATUS" value="Active" />
        <KPICardBlue title="LOCATIONS" subtitle="CONFIGURED" value="1" />
        <KPICardBlue title="USERS" subtitle="TEAM SIZE" value="1" />
        <KPICardBlue title="API CALLS" subtitle="THIS MONTH" value="245" />
      </div>

      {/* Settings Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg p-5 border border-[#D6E2F3]">
          <h3 className="text-xs font-bold text-[#4F80F5] uppercase tracking-wider mb-4">Business Information</h3>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-[#6B7280] uppercase font-medium">Business Name</label>
              <div className="mt-1 px-3 py-2 bg-[#E9F0FB] rounded text-sm text-[#1F2937] font-medium">Demo Dental Clinic</div>
            </div>
            <div>
              <label className="text-xs text-[#6B7280] uppercase font-medium">Plan</label>
              <div className="mt-1 px-3 py-2 bg-[#E9F0FB] rounded text-sm text-[#1F2937] font-medium capitalize">{planName}</div>
            </div>
            <div>
              <label className="text-xs text-[#6B7280] uppercase font-medium">Client ID</label>
              <div className="mt-1 px-3 py-2 bg-[#E9F0FB] rounded text-xs text-[#1F2937] font-mono">client_demo_123</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-5 border border-[#D6E2F3]">
          <h3 className="text-xs font-bold text-[#4F80F5] uppercase tracking-wider mb-4">Notification Preferences</h3>
          <div className="space-y-4">
            <ToggleSetting label="Email notifications" description="Daily summaries and alerts" enabled={emailNotifs} onChange={setEmailNotifs} />
            <ToggleSetting label="SMS alerts" description="Critical alerts only" enabled={smsNotifs} onChange={setSmsNotifs} />
            <ToggleSetting label="Usage alerts" description="When approaching limits" enabled={usageAlerts} onChange={setUsageAlerts} />
          </div>
        </div>

        <div className="bg-white rounded-lg p-5 border border-[#D6E2F3]">
          <h3 className="text-xs font-bold text-[#4F80F5] uppercase tracking-wider mb-4">Locations</h3>
          <div className="p-4 bg-[#E9F0FB] rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#6EDBC5] to-[#9AF2B4] flex items-center justify-center">
                <MapPin className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="font-semibold text-[#1F2937] text-sm">Main Office</p>
                <p className="text-xs text-[#6B7280]">Primary location</p>
              </div>
            </div>
          </div>
          <p className="text-xs text-[#6B7280] mt-3">Contact support to add or modify locations.</p>
        </div>

        <div className="bg-[#E9F0FB] rounded-lg p-5 border border-[#D6E2F3]">
          <h3 className="text-xs font-bold text-[#4F80F5] uppercase tracking-wider mb-4">Protected Settings</h3>
          <p className="text-xs text-[#6B7280] mb-4">The following are managed by Calyro:</p>
          <ul className="space-y-2">
            <li className="flex items-center gap-2 text-xs text-[#4B5563]"><XCircle className="w-3 h-3" /> AI conversation logic</li>
            <li className="flex items-center gap-2 text-xs text-[#4B5563]"><XCircle className="w-3 h-3" /> Voice and scripts</li>
            <li className="flex items-center gap-2 text-xs text-[#4B5563]"><XCircle className="w-3 h-3" /> Agent configuration</li>
            <li className="flex items-center gap-2 text-xs text-[#4B5563]"><XCircle className="w-3 h-3" /> Billing bypass</li>
          </ul>
          <p className="text-xs text-[#6B7280] mt-3">Contact support for any changes.</p>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// PREMIUM HELPER COMPONENTS
// ════════════════════════════════════════════════════════════════════════════

const AgentStatusBadge = ({ status, compact }) => {
  const config = {
    live: { bg: 'bg-[#6EDBC5]', bgLight: 'bg-[#6EDBC5]/10', text: 'text-[#4F80F5]', label: 'Live', sublabel: 'Accepting calls' },
    limited: { bg: 'bg-amber-500', bgLight: 'bg-amber-100', text: 'text-amber-700', label: 'Limited', sublabel: 'Nearing cap' },
    blocked: { bg: 'bg-red-500', bgLight: 'bg-red-100', text: 'text-red-700', label: 'Blocked', sublabel: 'Action required' },
  };
  const c = config[status] || config.live;
  
  if (compact) {
    return (
      <div className={`flex items-center gap-2 px-3 py-1.5 ${c.bgLight} rounded-lg`}>
        <span className={`relative w-2 h-2 ${c.bg} rounded-full ${status === 'live' ? 'animate-pulse' : ''}`}></span>
        <span className={`text-sm font-medium ${c.text}`}>{c.label}</span>
      </div>
    );
  }

  return (
    <div className="p-3 bg-white/10 rounded-lg">
      <div className="flex items-center gap-2">
        <span className={`relative w-2.5 h-2.5 ${c.bg} rounded-full ${status === 'live' ? 'animate-pulse' : ''}`}></span>
        <span className="font-semibold text-white text-sm">Agent {c.label}</span>
      </div>
      <p className="text-xs mt-1 text-[#8eb8d4] ml-4">{c.sublabel}</p>
    </div>
  );
};

// New KPI Card with exact color palette
const KPICardBlue = ({ title, subtitle, value }) => (
  <div className="card-hover bg-white rounded-xl p-5 shadow-sm border border-[#D6E2F3]">
    <p className="text-xs font-bold text-[#4F80F5] uppercase tracking-wider">{title}</p>
    <p className="text-xs text-[#8A8A8A] uppercase">{subtitle}</p>
    <p className="text-4xl font-bold text-[#4A4A4A] mt-3">{value}</p>
  </div>
);

// Line Chart Component with exact color palette
const LineChartComponent = ({ data }) => {
  const maxVal = Math.max(...data.map(d => d.value));
  const points = data.map((d, i) => {
    const x = 50 + (i * 120);
    const y = 180 - ((d.value / maxVal) * 150);
    return { x, y, value: d.value, month: d.month };
  });
  
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  
  return (
    <div className="relative h-56">
      <svg viewBox="0 0 700 200" className="w-full h-full">
        {/* Grid lines */}
        {[100, 200, 300, 400].map((val, i) => (
          <g key={i}>
            <line x1="40" y1={180 - (val / maxVal) * 150} x2="680" y2={180 - (val / maxVal) * 150} stroke="#D6E2F3" strokeWidth="1" />
            <text x="25" y={185 - (val / maxVal) * 150} fill="#8A8A8A" fontSize="10">{val}</text>
          </g>
        ))}
        
        {/* Line */}
        <path d={pathD} fill="none" stroke="#2F6FE4" strokeWidth="2" className="animate-draw-line" />
        
        {/* Data points with labels */}
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="6" fill="#4F80F5" />
            <circle cx={p.x} cy={p.y} r="3" fill="white" />
            {(i === 1 || i === 4) && (
              <g>
                <rect x={p.x - 25} y={p.y - 30} width="50" height="20" rx="4" fill="#6EDBC5" />
                <text x={p.x} y={p.y - 16} textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">{p.value.toLocaleString()}</text>
              </g>
            )}
            <text x={p.x} y="198" textAnchor="middle" fill="#4A4A4A" fontSize="11" fontWeight="500">{p.month}</text>
          </g>
        ))}
      </svg>
    </div>
  );
};

// Area Chart Card with exact color palette
const AreaChartCard = ({ title, subtitle, color1, color2 }) => {
  const gradientId = `gradient-${title.replace(/\s/g, '')}`;
  // Use the exact gradient colors
  const c1 = color1 || '#4F80F5';
  const c2 = color2 || '#6EDBC5';
  return (
    <div className="card-hover bg-white rounded-xl p-5 shadow-sm border border-[#D6E2F3]">
      <h4 className="text-xs font-bold text-[#4F80F5] uppercase tracking-wider">{title}</h4>
      <p className="text-xs text-[#8A8A8A] uppercase mb-3">{subtitle}</p>
      <p className="text-[#8A8A8A] text-xs mb-4 line-clamp-2">Lorem ipsum dolor sit amet, consectetur adipiscing elit.</p>
      <div className="h-24">
        <svg viewBox="0 0 200 80" className="w-full h-full">
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#4F80F5" stopOpacity="0.8" />
              <stop offset="50%" stopColor="#6EDBC5" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#9AF2B4" stopOpacity="0.4" />
            </linearGradient>
          </defs>
          <path 
            d="M 0 60 Q 30 40 50 50 T 100 30 T 150 45 T 200 20 L 200 80 L 0 80 Z" 
            fill={`url(#${gradientId})`}
          />
          <path 
            d="M 0 60 Q 30 40 50 50 T 100 30 T 150 45 T 200 20" 
            fill="none" 
            stroke="#2F6FE4" 
            strokeWidth="2"
          />
        </svg>
      </div>
      <div className="flex items-center gap-4 mt-2 text-xs">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#4F80F5]"></span> <span className="text-[#8A8A8A]">Series A</span></span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#6EDBC5]"></span> <span className="text-[#8A8A8A]">Series B</span></span>
      </div>
    </div>
  );
};

// Activity List Card with exact color palette
const ActivityListCard = ({ alerts }) => (
  <div className="card-hover bg-white rounded-xl p-5 shadow-sm border border-[#D6E2F3]">
    <h4 className="text-xs font-bold text-[#4F80F5] uppercase tracking-wider">Recent Activity</h4>
    <p className="text-xs text-[#8A8A8A] uppercase mb-4">DOLOR SIT AMET</p>
    <div className="space-y-3">
      {['Call completed', 'Lead captured', 'Booking made', 'Usage alert'].map((item, i) => (
        <div key={i} className="flex items-center justify-between py-2 border-b border-[#D6E2F3] last:border-0">
          <div>
            <p className="text-xs font-semibold text-[#4F80F5] uppercase">{item}</p>
            <p className="text-xs text-[#8A8A8A]">Status update notification</p>
          </div>
          <button className="px-3 py-1 text-xs font-medium text-[#6EDBC5] bg-[#6EDBC5]/10 rounded hover:bg-[#6EDBC5]/20 transition-colors">
            SEE MORE
          </button>
        </div>
      ))}
    </div>
    <div className="mt-4 space-y-2">
      <div className="flex items-center justify-between text-xs">
        <span className="text-[#8A8A8A]">LOREM</span>
        <div className="flex-1 mx-3 h-2 bg-[#E9F0FB] rounded-full overflow-hidden">
          <div className="h-full w-3/4 bg-gradient-to-r from-[#4F80F5] to-[#6EDBC5] rounded-full"></div>
        </div>
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-[#8A8A8A]">LOREM</span>
        <div className="flex-1 mx-3 h-2 bg-[#E9F0FB] rounded-full overflow-hidden">
          <div className="h-full w-1/2 bg-gradient-to-r from-[#6EDBC5] to-[#9AF2B4] rounded-full"></div>
        </div>
      </div>
    </div>
  </div>
);

const KPICard = ({ title, value, subtitle, icon: Icon, color, progress, statusBadge }) => {
  return (
    <div className="card-hover bg-white rounded-xl border border-[#d4e8f2] p-5 transition-all">
      <div className="flex items-start justify-between">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#6EDBC5] to-[#9AF2B4] flex items-center justify-center shadow-lg shadow-[#6EDBC5]/20">
          <Icon className="w-6 h-6 text-white" />
        </div>
        {statusBadge && (
          <span className={`relative w-3 h-3 rounded-full ${color === 'emerald' ? 'bg-[#6EDBC5] animate-pulse' : color === 'amber' ? 'bg-amber-500' : 'bg-red-500'}`}></span>
        )}
      </div>
      <p className="text-[#6B7280] text-xs font-medium uppercase tracking-wider mt-4">{title}</p>
      <p className="text-3xl font-bold text-[#4A4A4A] mt-1 animate-count-up">{value}</p>
      <p className="text-xs text-[#9CA3AF] mt-1">{subtitle}</p>
      {progress !== undefined && (
        <div className="mt-3 h-2 bg-[#e8f4fc] rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full transition-all duration-1000 bg-gradient-to-r from-[#6EDBC5] to-[#9AF2B4]" 
            style={{ width: `${Math.min(100, progress)}%` }} 
          />
        </div>
      )}
    </div>
  );
};

const AnimatedBarChart = ({ data }) => {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const maxVal = Math.max(...data, 1);
  
  return (
    <div className="h-48 flex items-end justify-between gap-3">
      {days.map((day, i) => {
        const val = data[i];
        const height = Math.max((val / maxVal) * 100, 8);
        const isToday = i === new Date().getDay() - 1 || (new Date().getDay() === 0 && i === 6);
        return (
          <div key={day} className="flex-1 flex flex-col items-center gap-3">
            <span className="text-xs font-semibold text-slate-500">{val}</span>
            <div className="w-full h-36 flex items-end">
              <div 
                className={`w-full rounded-xl transition-all duration-700 animate-chart-grow ${isToday ? 'bg-gradient-to-t from-indigo-600 to-purple-500 shadow-lg shadow-indigo-500/30' : 'bg-gradient-to-t from-slate-200 to-slate-100 hover:from-indigo-400 hover:to-indigo-300'}`}
                style={{ height: `${height}%`, animationDelay: `${i * 100}ms` }}
              />
            </div>
            <span className={`text-xs font-semibold ${isToday ? 'text-indigo-600' : 'text-slate-400'}`}>{day}</span>
          </div>
        );
      })}
    </div>
  );
};

const UsageDonutChart = ({ used, total }) => {
  const percent = Math.min((used / total) * 100, 100) || 75;
  const circumference = 2 * Math.PI * 60;
  const strokeDashoffset = circumference - (percent / 100) * circumference;
  
  return (
    <div className="flex flex-col items-center justify-center h-full">
      <div className="relative w-36 h-36">
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 150 150">
          {/* Background circle */}
          <circle cx="75" cy="75" r="60" stroke="#D6E2F3" strokeWidth="15" fill="none" />
          {/* Progress circle - Mint Teal gradient */}
          <circle 
            cx="75" cy="75" r="60" 
            stroke="url(#donutGradientNew)" 
            strokeWidth="15" 
            fill="none" 
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-1000"
          />
          <defs>
            <linearGradient id="donutGradientNew" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#4F80F5" />
              <stop offset="50%" stopColor="#6EDBC5" />
              <stop offset="100%" stopColor="#9AF2B4" />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <p className="text-3xl font-bold text-[#6EDBC5]">{Math.round(percent)}%</p>
            <p className="text-xs text-[#8A8A8A]">used</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatBlock = ({ label, value, color }) => {
  const colors = { indigo: 'text-indigo-600', slate: 'text-slate-600', emerald: 'text-emerald-600' };
  return (
    <div className="text-center p-4 bg-slate-50 rounded-2xl">
      <p className={`text-2xl font-bold ${colors[color] || 'text-slate-900'}`}>{value}</p>
      <p className="text-xs text-slate-500 mt-1 font-medium">{label}</p>
    </div>
  );
};

const UsagePercentBadge = ({ percent, large }) => {
  const color = percent >= 100 ? 'bg-red-100 text-red-700' : percent >= 90 ? 'bg-orange-100 text-orange-700' : percent >= 70 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700';
  return (
    <span className={`px-4 py-2 rounded-xl font-bold ${color} ${large ? 'text-lg' : 'text-sm'}`}>
      {percent}% used
    </span>
  );
};

const UsageProgressBarEnhanced = ({ used, included, hardCap }) => {
  const pctUsed = Math.min((used / hardCap) * 100, 100);
  const pctIncluded = (included / hardCap) * 100;
  const color = pctUsed >= pctIncluded ? 'bg-gradient-to-r from-red-500 to-rose-500' : pctUsed >= pctIncluded * 0.9 ? 'bg-gradient-to-r from-orange-500 to-amber-500' : pctUsed >= pctIncluded * 0.7 ? 'bg-gradient-to-r from-amber-500 to-yellow-500' : 'bg-gradient-to-r from-emerald-500 to-teal-500';

  return (
    <div className="relative">
      {/* Background with zones */}
      <div className="h-5 bg-slate-100 rounded-full overflow-hidden shadow-inner">
        <div className="absolute inset-0 flex rounded-full overflow-hidden">
          <div className="w-[70%] bg-emerald-100/50"></div>
          <div className="w-[20%] bg-amber-100/50"></div>
          <div className="w-[10%] bg-red-100/50"></div>
        </div>
        {/* Included marker */}
        <div className="absolute top-0 bottom-0 w-0.5 bg-slate-400" style={{ left: `${pctIncluded}%` }}></div>
        {/* Usage bar */}
        <div className={`relative h-full ${color} rounded-full transition-all duration-1000 shadow-lg`} style={{ width: `${pctUsed}%` }}>
          <div className="absolute inset-0 shimmer-bg rounded-full"></div>
        </div>
      </div>
      {/* Labels */}
      <div className="flex justify-between mt-2 text-xs font-medium text-slate-400">
        <span>0</span>
        <span style={{ marginLeft: `${pctIncluded - 5}%` }}>Included ({included})</span>
        <span>{hardCap}</span>
      </div>
    </div>
  );
};

const FilterSelect = ({ value, onChange, options }) => (
  <select 
    value={value} 
    onChange={(e) => onChange(e.target.value)} 
    className="px-4 py-2.5 bg-white border border-[#d4e8f2] rounded-lg text-[#4A4A4A] font-medium focus:outline-none focus:ring-2 focus:ring-[#6EDBC5]/20 focus:border-[#6EDBC5] transition-all cursor-pointer"
  >
    {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
  </select>
);

const IntentBadge = ({ intent }) => (
  <span className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-[#e8f4fc] text-[#4B5563]">
    {intent || 'General'}
  </span>
);

const OutcomeBadge = ({ outcome }) => {
  const config = {
    booked: 'bg-[#6EDBC5]/10 text-[#4F80F5] border-[#6EDBC5]/30',
    lead: 'bg-[#2196f3]/10 text-[#1976d2] border-[#2196f3]/30',
    info: 'bg-[#e8f4fc] text-[#6B7280] border-[#d4e8f2]',
  };
  return (
    <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold capitalize border ${config[outcome] || config.info}`}>
      {outcome || 'Info'}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const config = {
    new: 'bg-[#6EDBC5]/10 text-[#4F80F5] border-[#6EDBC5]/30',
    contacted: 'bg-[#2196f3]/10 text-[#1976d2] border-[#2196f3]/30',
    qualified: 'bg-[#4A4A4A]/10 text-[#4A4A4A] border-[#4A4A4A]/30',
  };
  return (
    <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold capitalize border ${config[status] || 'bg-[#e8f4fc] text-[#6B7280] border-[#d4e8f2]'}`}>
      {status || 'New'}
    </span>
  );
};

const BookingStatusBadge = ({ status }) => {
  const config = {
    confirmed: 'bg-[#6EDBC5]/10 text-[#4F80F5]',
    proposed: 'bg-amber-100 text-amber-700',
    pending: 'bg-[#e8f4fc] text-[#6B7280]',
  };
  return (
    <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold capitalize ${config[status] || config.pending}`}>
      {status || 'Pending'}
    </span>
  );
};

const InvoiceStatusBadge = ({ status }) => (
  <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${status === 'paid' ? 'bg-[#6EDBC5]/10 text-[#4F80F5]' : 'bg-amber-100 text-amber-700'}`}>
    {status === 'paid' ? 'Paid' : 'Pending'}
  </span>
);

const MetricCard = ({ color, label, value, icon: Icon }) => {
  return (
    <div className="card-hover bg-white rounded-xl border border-[#d4e8f2] p-5">
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#6EDBC5] to-[#9AF2B4] flex items-center justify-center shadow-lg shadow-[#6EDBC5]/20 mb-3">
        <Icon className="w-5 h-5 text-white" />
      </div>
      <p className="text-[#6B7280] text-xs font-medium uppercase">{label}</p>
      <p className="text-3xl font-bold text-[#4A4A4A] mt-1">{value}</p>
    </div>
  );
};

const AlertItem = ({ type, message }) => {
  const config = {
    success: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', icon: CheckCircle, iconColor: 'text-emerald-500' },
    warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', icon: AlertTriangle, iconColor: 'text-amber-500' },
    critical: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', icon: AlertCircle, iconColor: 'text-red-500' },
  };
  const c = config[type] || config.warning;
  const Icon = c.icon;
  return (
    <div className={`flex items-center gap-4 p-5 ${c.bg} ${c.border} border-2 rounded-2xl`}>
      <Icon className={`w-6 h-6 ${c.iconColor} flex-shrink-0`} />
      <p className={`font-semibold ${c.text}`}>{message}</p>
    </div>
  );
};

const UsageStatBox = ({ label, value, unit, highlight }) => (
  <div className={`text-center p-5 rounded-2xl ${highlight ? 'bg-emerald-50 border-2 border-emerald-200' : 'bg-slate-50'}`}>
    <p className={`text-3xl font-bold ${highlight ? 'text-emerald-600' : 'text-slate-900'}`}>{value}</p>
    <p className="text-sm text-slate-500 mt-1 font-medium">{label}</p>
  </div>
);

const UsageZoneItem = ({ color, label, range, description }) => {
  const colors = { emerald: 'bg-emerald-500', amber: 'bg-amber-500', orange: 'bg-orange-500', red: 'bg-red-500' };
  return (
    <div className="flex items-center gap-4 p-3 hover:bg-slate-50 rounded-xl transition-colors">
      <div className={`w-4 h-4 rounded-full ${colors[color]}`}></div>
      <div className="flex-1">
        <div className="flex justify-between">
          <span className="font-semibold text-slate-700">{label}</span>
          <span className="text-sm text-slate-500">{range}</span>
        </div>
        <p className="text-sm text-slate-400">{description}</p>
      </div>
    </div>
  );
};

const StatusRow = ({ label, status }) => {
  const config = {
    paid: { bg: 'bg-[#6EDBC5]/10', text: 'text-[#4F80F5]', label: 'Paid' },
    active: { bg: 'bg-[#6EDBC5]/10', text: 'text-[#4F80F5]', label: 'Active' },
    ok: { bg: 'bg-[#6EDBC5]/10', text: 'text-[#4F80F5]', label: 'Ok' },
    grace: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Grace' },
    blocked: { bg: 'bg-red-100', text: 'text-red-700', label: 'Blocked' },
  };
  const c = config[status] || { bg: 'bg-[#e8f4fc]', text: 'text-[#6B7280]', label: status };
  return (
    <div className="flex items-center justify-between py-2 border-b border-[#e8f4fc] last:border-0">
      <span className="text-[#4B5563] font-medium text-sm">{label}</span>
      <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${c.bg} ${c.text}`}>{c.label}</span>
    </div>
  );
};

const TimelineItem = ({ icon: Icon, color, label, value }) => {
  const colors = {
    cyan: 'bg-[#6EDBC5]/10 text-[#6EDBC5]',
    navy: 'bg-[#4A4A4A]/10 text-[#4A4A4A]',
    emerald: 'bg-emerald-100 text-emerald-600',
    slate: 'bg-[#e8f4fc] text-[#6B7280]',
    amber: 'bg-amber-100 text-amber-600',
    indigo: 'bg-[#6EDBC5]/10 text-[#6EDBC5]',
  };
  return (
    <div className="flex items-center gap-3 py-2">
      <div className={`w-9 h-9 rounded-lg ${colors[color] || colors.cyan} flex items-center justify-center`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <p className="text-xs text-[#4A4A4A]/50">{label}</p>
        <p className="font-semibold text-[#4A4A4A] text-sm">{value}</p>
      </div>
    </div>
  );
};

const BillingRow = ({ label, value, highlight, bold }) => (
  <div className="flex justify-between items-center py-2">
    <span className={`text-sm ${bold ? 'font-bold text-[#4A4A4A]' : 'text-[#6B7280]'}`}>{label}</span>
    <span className={`font-bold ${highlight ? 'text-orange-600' : bold ? 'text-xl text-[#4A4A4A]' : 'text-[#4A4A4A]'}`}>{value}</span>
  </div>
);

const SettingsField = ({ label, value, readOnly, mono }) => (
  <div className="space-y-2">
    <label className="text-sm font-semibold text-slate-500 uppercase tracking-wider">{label}</label>
    <div className={`px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 ${mono ? 'font-mono text-sm' : 'font-medium'}`}>
      {value}
    </div>
  </div>
);

const ToggleSetting = ({ label, description, enabled, onChange }) => (
  <div className="flex items-center justify-between py-2">
    <div>
      <p className="font-semibold text-[#1F2937] text-sm">{label}</p>
      <p className="text-xs text-[#6B7280]">{description}</p>
    </div>
    <button
      onClick={() => onChange(!enabled)}
      className={`relative w-12 h-6 rounded-full transition-all duration-300 ${enabled ? 'bg-[#6EDBC5]' : 'bg-[#D6E2F3]'}`}
    >
      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-all duration-300 ${enabled ? 'left-6' : 'left-0.5'}`}></span>
    </button>
  </div>
);

const formatDuration = (seconds) => {
  if (!seconds) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export default EnterpriseDashboard;
